if (!customElements.get("m-featured-collection")) {
  class MFeaturedCollection extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.resizeHandler = null;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isTouching = false;
    }

    connectedCallback() {
      this.selectors = {
        loadMoreBtn: "[data-load-more]",
        loadMoreBtnWrapper: ".m-featured-collection__button",
        productsContainer: "[data-products-container]",
        products: [".m-product-card"],
        soldNumber: ["[data-sold-number]"],
        availableNumber: ["[data-available-number]"],
        countDown: "[data-flashsale-countdown]",
        slideControls: ".m-slider-controls",
        slideContainer: "[data-custom-slider-container]",
      };

      this.domNodes = queryDomNodes(this.selectors, this);

      this.buttonType = this.dataset.buttonType;
      this.infiniteLoad = this.dataset.infiniteLoad;
      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.enableFlashsale = this.dataset.enableFlashsale === "true";
      this.enableCountdown = this.dataset.enableCountdown === "true";
      this.id = this.dataset.id;
      this.items = parseInt(this.dataset.items || "4", 10);

      this.canLoad = true;
      this.currentPage = 1;
      this.spinner = spinner();

      if (this.enableSlider) {
        this.initCustomSlider();
      }

      document.addEventListener("matchMobile", () => {
        if (this.enableSlider) this.recalculateSlider(false);
      });

      document.addEventListener("unmatchMobile", () => {
        if (this.enableSlider) this.recalculateSlider(false);
      });

      if (this.enableFlashsale) {
        this.initFlashSale();
      }

      if (!this.enableSlider && this.buttonType === "load") {
        this.initLoadMore();

        if (this.infiniteLoad === "true") {
          this.initInfiniteLoad();
        }
      }
    }

    disconnectedCallback() {
      this.destroyCustomSlider();
    }

    initCustomSlider() {
      this.destroyCustomSlider();

      const slideContainer = this.querySelector("[data-custom-slider-container]");
      const track = this.querySelector("[data-products-container]");
      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const pagination = controlsContainer && controlsContainer.querySelector(".m-custom-slider-pagination");

      if (!slideContainer || !track) return;

      this.slider = {
        slideContainer,
        track,
        controlsContainer,
        prevButton,
        nextButton,
        pagination,
        currentPage: 0,
        totalPages: 1,
        pageWidth: 0,
        maxTranslate: 0,
      };

      if (prevButton) {
        this.slider.prevHandler = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.goToSliderPage(this.slider.currentPage - 1);
        };

        prevButton.addEventListener("click", this.slider.prevHandler, true);
      }

      if (nextButton) {
        this.slider.nextHandler = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.goToSliderPage(this.slider.currentPage + 1);
        };

        nextButton.addEventListener("click", this.slider.nextHandler, true);
      }

      this.slider.touchStartHandler = (event) => this.onSliderTouchStart(event);
      this.slider.touchEndHandler = (event) => this.onSliderTouchEnd(event);

      slideContainer.addEventListener("touchstart", this.slider.touchStartHandler, { passive: true });
      slideContainer.addEventListener("touchend", this.slider.touchEndHandler, { passive: true });

      this.resizeHandler = () => this.recalculateSlider(false);
      window.addEventListener("resize", this.resizeHandler);

      setTimeout(() => {
        this.setControlsOffset(controlsContainer);
        this.recalculateSlider(false);
      }, 100);

      setTimeout(() => {
        this.setControlsOffset(controlsContainer);
        this.recalculateSlider(false);
      }, 500);
    }

    destroyCustomSlider() {
      if (this.slider) {
        if (this.slider.prevButton && this.slider.prevHandler) {
          this.slider.prevButton.removeEventListener("click", this.slider.prevHandler, true);
        }

        if (this.slider.nextButton && this.slider.nextHandler) {
          this.slider.nextButton.removeEventListener("click", this.slider.nextHandler, true);
        }

        if (this.slider.slideContainer && this.slider.touchStartHandler) {
          this.slider.slideContainer.removeEventListener("touchstart", this.slider.touchStartHandler);
        }

        if (this.slider.slideContainer && this.slider.touchEndHandler) {
          this.slider.slideContainer.removeEventListener("touchend", this.slider.touchEndHandler);
        }

        if (this.slider.track) {
          this.slider.track.style.transform = "";
          this.slider.track.style.transitionDuration = "";
        }
      }

      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
        this.resizeHandler = null;
      }

      this.slider = null;
    }

    isMobileSlider() {
      return window.innerWidth < 768;
    }

    getSlides() {
      if (!this.slider || !this.slider.track) return [];

      return Array.from(this.slider.track.children).filter((child) => {
        return child.classList.contains("m-custom-slider__item");
      });
    }

    getTrackColumnGap() {
      if (!this.slider || !this.slider.track) return 0;

      const trackStyles = window.getComputedStyle(this.slider.track);
      return parseFloat(trackStyles.columnGap || trackStyles.gap || "0") || 0;
    }

    getDesktopStepWidth(slides) {
      const desktopProductsPerClick = 2;
      const firstSlide = slides[0];

      if (!firstSlide) {
        return this.slider.slideContainer.getBoundingClientRect().width;
      }

      const slideWidth = firstSlide.getBoundingClientRect().width;
      const columnGap = this.getTrackColumnGap();

      return Math.max(1, (slideWidth + columnGap) * desktopProductsPerClick);
    }

    getMobileStepWidth(slides) {
      const mobileProductsPerClick = 2;
      const firstSlide = slides[0];

      if (!firstSlide) {
        return this.slider.slideContainer.getBoundingClientRect().width;
      }

      const slideWidth = firstSlide.getBoundingClientRect().width;
      const columnGap = this.getTrackColumnGap();

      return Math.max(1, (slideWidth + columnGap) * mobileProductsPerClick);
    }

    getSliderMetrics() {
      const slides = this.getSlides();
      const slideCount = slides.length;
      const containerWidth = this.slider.slideContainer.getBoundingClientRect().width;
      const trackScrollWidth = this.slider.track.scrollWidth;
      const maxTranslate = Math.max(0, trackScrollWidth - containerWidth);

      if (this.isMobileSlider()) {
        /*
          Mobile is now 1 row with 2 visible products.
          Each arrow click/swipe advances by 2 products.
        */
        const productsPerMobilePage = 2;
        const totalPages = Math.max(1, Math.ceil(slideCount / productsPerMobilePage));
        const pageWidth = this.getMobileStepWidth(slides);

        return {
          slideCount,
          totalPages,
          pageWidth,
          maxTranslate,
        };
      }

      const pageWidth = this.getDesktopStepWidth(slides);
      const totalPages = Math.max(1, Math.ceil(maxTranslate / pageWidth) + 1);

      return {
        slideCount,
        totalPages,
        pageWidth,
        maxTranslate,
      };
    }

    recalculateSlider(animate = false) {
      if (!this.slider) return;

      const metrics = this.getSliderMetrics();

      this.slider.totalPages = metrics.totalPages;
      this.slider.pageWidth = metrics.pageWidth;
      this.slider.maxTranslate = metrics.maxTranslate;

      const safePage = Math.max(0, Math.min(this.slider.currentPage, this.slider.totalPages - 1));

      this.goToSliderPage(safePage, animate);
    }

    goToSliderPage(page, animate = true) {
      if (!this.slider) return;

      const metrics = this.getSliderMetrics();
      const maxPage = metrics.totalPages - 1;
      const safePage = Math.max(0, Math.min(page, maxPage));
      const rawTranslate = safePage * metrics.pageWidth;
      const translate = Math.min(rawTranslate, metrics.maxTranslate);

      this.slider.currentPage = safePage;
      this.slider.totalPages = metrics.totalPages;
      this.slider.pageWidth = metrics.pageWidth;
      this.slider.maxTranslate = metrics.maxTranslate;

      this.slider.track.style.transitionDuration = animate ? "300ms" : "0ms";
      this.slider.track.style.transform = `translate3d(-${translate}px, 0px, 0px)`;

      this.updateSliderControls();
    }

    updateSliderControls() {
      if (!this.slider) return;

      const { controlsContainer, prevButton, nextButton, pagination, currentPage, totalPages } = this.slider;
      const shouldShowControls = this.showNavigation && totalPages > 1;

      if (controlsContainer) {
        controlsContainer.classList.toggle("m:hidden", !shouldShowControls);
      }

      if (!shouldShowControls) return;

      const atStart = currentPage <= 0;
      const atEnd = currentPage >= totalPages - 1;

      if (prevButton) {
        prevButton.classList.toggle("m:hidden", atStart);
        prevButton.classList.toggle("is-disabled", atStart);
        prevButton.toggleAttribute("disabled", atStart);
      }

      if (nextButton) {
        nextButton.classList.toggle("m:hidden", atEnd);
        nextButton.classList.toggle("is-disabled", atEnd);
        nextButton.toggleAttribute("disabled", atEnd);
      }

      if (pagination) {
        pagination.textContent = `${currentPage + 1} / ${totalPages}`;
      }
    }

    onSliderTouchStart(event) {
      if (!this.slider || !event.changedTouches || !event.changedTouches.length) return;

      this.isTouching = true;
      this.touchStartX = event.changedTouches[0].clientX;
      this.touchStartY = event.changedTouches[0].clientY;
    }

    onSliderTouchEnd(event) {
      if (!this.slider || !this.isTouching || !event.changedTouches || !event.changedTouches.length) return;

      this.isTouching = false;

      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      const diffX = touchEndX - this.touchStartX;
      const diffY = touchEndY - this.touchStartY;
      const minimumSwipeDistance = 40;

      if (Math.abs(diffY) > Math.abs(diffX)) return;
      if (Math.abs(diffX) < minimumSwipeDistance) return;

      if (diffX < 0) {
        this.goToSliderPage(this.slider.currentPage + 1);
      } else {
        this.goToSliderPage(this.slider.currentPage - 1);
      }
    }

    setControlsOffset(controlsContainer) {
      const firstItem = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");

      if (firstItem && controlsContainer) {
        const itemHeight = firstItem.clientHeight;
        controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");
      }
    }

    initLoadMore() {
      this.triggerLoad = false;
      this.totalPages = parseInt(this.dataset.totalPages);

      addEventDelegate({
        context: this.container,
        selector: this.selectors.loadMoreBtn,
        handler: (e) => {
          e.preventDefault();
          this.handleLoadMore();
        },
      });
    }

    initInfiniteLoad() {
      const maxPages = this.dataset.maxPages;

      window.addEventListener("scroll", () => {
        this.canLoad = this.currentPage < parseInt(maxPages);

        if (!this.canLoad) return;

        if (this.offsetTop + this.clientHeight - window.innerHeight < window.scrollY && !this.triggerLoad) {
          this.triggerLoad = true;
          this.handleLoadMore();
        }
      });
    }

    async handleLoadMore() {
      this.currentPage++;
      this.canLoad = this.currentPage < this.totalPages;
      this.toggleLoading(true);

      const collectionID = this.dataset.collectionId;
      const res = await fetchCountDown(collectionID);

      const url = this.dataset.url;
      const dataUrl = `${url}?page=${this.currentPage}&section_id=${this.id}`;

      fetchCache(dataUrl).then((html) => {
        this.toggleLoading(false);

        const dom = generateDomFromString(html);
        const products = dom.querySelector(this.selectors.productsContainer);
        const oldCards = this.domNodes.productsContainer.childElementCount;
        let isAppended = false;

        if (products) {
          Array.from(products.childNodes).forEach((product) => {
            this.domNodes.productsContainer.appendChild(product);

            const check = setInterval(() => {
              if (this.domNodes.productsContainer.childElementCount > oldCards) {
                clearInterval(check);
                isAppended = true;
              }
            }, 50);
          });
        }

        if (this.enableFlashsale && res.ok && res.payload && res.payload.length) {
          const { expires_date } = res.payload[0];
          this.expires_date = expires_date;

          const check = setInterval(() => {
            if (isAppended) {
              clearInterval(check);

              const cards = this.domNodes.productsContainer.querySelectorAll(".m-product-card");

              cards.forEach((card) => {
                const content = card.querySelector(".m-product-card__content");
                const progress = card.querySelector(".m-product-sale-progress");

                if (!progress) {
                  let soldNumber;

                  if (card.dataset.soldNumber) {
                    soldNumber = card.dataset.soldNumber;
                  }

                  const component = saleProgress(res.payload[0], card.dataset.productId, soldNumber);
                  const newComponent = generateDomFromString(component);

                  content.appendChild(newComponent);
                }
              });
            }
          }, 50);
        }

        this.triggerLoad = false;

        if (!this.canLoad) {
          this.domNodes.loadMoreBtn && this.domNodes.loadMoreBtn.classList.add("m:hidden");
        }
      });
    }

    toggleLoading(status) {
      if (!this.domNodes.loadMoreBtn) return;

      if (status) {
        this.domNodes.loadMoreBtn.classList.add("m-spinner-loading");
      } else {
        this.domNodes.loadMoreBtn.classList.remove("m-spinner-loading");
      }
    }

    async initFlashSale() {
      const collectionID = this.dataset.collectionId;
      const res = await fetchCountDown(collectionID);

      if (res.ok && res.payload && res.payload.length) {
        const { expires_date } = res.payload[0];
        this.expires_date = expires_date;

        this.domNodes.products.forEach((card) => {
          const content = card.querySelector(".m-product-card__content");
          const pcardSale = card.querySelector(".m-product-sale-progress");
          let soldNumber;

          if (card.dataset.soldNumber) {
            soldNumber = card.dataset.soldNumber;
          }

          const component = saleProgress(res.payload[0], card.dataset.productId, soldNumber);
          const newComponent = generateDomFromString(component);

          if (!pcardSale) {
            content.appendChild(newComponent);
          }
        });

        this.initCountDown();
      }
    }

    initCountDown() {
      const endTime = new Date(this.expires_date).getTime();

      this.countDownTimer = new CountdownTimer(this.domNodes.countDown, Date.now(), endTime, {
        loop: true,
      });
    }
  }

  customElements.define("m-featured-collection", MFeaturedCollection);
}